import React, { useCallback, useMemo } from 'react'
import { Box, SxProps, TextField, TextFieldProps, Theme } from '@mui/material'
import { useField } from 'formik'
import { useAppTranslations } from '../../utils/hooks/useAppTranslations'
import HelperTooltip from './HelperTooltip'
import { trimFieldOnBlur } from './trimFieldOnBlur'
import { isNil } from 'lodash-es'
import { compactFormFieldSx } from './DisplayedItem'

export interface InputFieldProps {
  name: string
  label: string
  type: 'text' | 'number' | 'email' | 'password' | 'url'
  autoFocus?: boolean
  fullWidth?: boolean
  size?: TextFieldProps['size']
  multiline?: boolean
  rows?: number
  margin?: TextFieldProps['margin']
  defaultHelperText?: string
  placeholder?: string
  required?: boolean
  InputProps?: TextFieldProps['InputProps']
  InputLabelProps?: TextFieldProps['InputLabelProps']
  sx?: SxProps<Theme>
  onMouseLeave?: () => void
  tooltipText?: string
  variant?: 'filled' | 'outlined' | 'standard'
  onFocus?: () => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  onChange?: (value: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  showErrorOnInit?: boolean
  compact?: boolean
}

const InputField: React.FC<InputFieldProps> = ({
  name,
  label,
  type,
  size = 'small',
  defaultHelperText = '',
  margin = 'normal',
  placeholder,
  onMouseLeave,
  InputLabelProps,
  InputProps,
  tooltipText,
  variant = 'standard',
  onChange,
  showErrorOnInit = false,
  onBlur,
  compact = false,
  sx,
  ...props
}) => {
  const t = useAppTranslations()
  const [field, meta, helpers] = useField({ name })

  const hasVisibleError = useMemo(
    () => !isNil(meta.error) && (meta.touched || showErrorOnInit),
    [meta.touched, meta.error, showErrorOnInit]
  )

  const helperText = useMemo(
    () => (hasVisibleError && meta.error && t(meta.error)) || defaultHelperText,
    [meta.error, hasVisibleError, defaultHelperText, t]
  )

  const handleChange = useCallback(
    (event) => {
      field.onChange(event)
      if (onChange !== undefined) onChange(event)
    },
    [field, onChange]
  )

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      trimFieldOnBlur(field, helpers)(event)
      onBlur?.(event)
    },
    [field, helpers, onBlur]
  )

  return (
    <Box onMouseLeave={onMouseLeave} sx={{ width: '100%' }}>
      <TextField
        id={name}
        type={type}
        hiddenLabel={compact}
        label={
          <>
            {t(label)}
            {tooltipText && <HelperTooltip helperText={tooltipText} />}
          </>
        }
        margin={compact ? 'none' : margin}
        variant={variant}
        size={size}
        error={hasVisibleError}
        helperText={helperText}
        placeholder={placeholder}
        fullWidth
        sx={compact ? { ...compactFormFieldSx, ...sx } : sx}
        {...field}
        {...props}
        onChange={handleChange}
        onBlur={handleBlur}
        slotProps={{
          inputLabel: compact
            ? { sx: { display: 'none' }, ...InputLabelProps }
            : {
                shrink: true,
                ...InputLabelProps,
              },
          input: InputProps,
        }}
      />
    </Box>
  )
}

export default InputField
